import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import {
  Activity,
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  LogIn,
  LogOut,
  UserMinus,
  Receipt,
  Send,
  CreditCard,
  Calendar,
  ScanLine,
  Newspaper,
  FileUp,
  Shield,
  ArrowRightLeft,
  KeyRound,
  UserPlus,
  Settings,
  X,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
  login: LogIn,
  login_failed: LogIn,
  logout: LogOut,
  member_status_changed: UserMinus,
  invoice_created: Receipt,
  invoice_sent: Send,
  invoice_paid: CreditCard,
  payment_received: CreditCard,
  event_registered: Calendar,
  event_cancelled: Calendar,
  event_checkin: ScanLine,
  newsletter_sent: Newspaper,
  document_uploaded: FileUp,
  document_deleted: FileUp,
  admin_impersonate: Shield,
  admin_change_approved: Shield,
  admin_change_rejected: Shield,
  sync_import: ArrowRightLeft,
  sync_export: ArrowRightLeft,
  password_setup: KeyRound,
  password_reset: KeyRound,
  membership_request: UserPlus,
  settings_updated: Settings,
};

const TYPE_COLORS = {
  login: 'text-green-400',
  login_failed: 'text-red-400',
  logout: 'text-muted-foreground',
  member_status_changed: 'text-orange-400',
  invoice_created: 'text-blue-400',
  invoice_sent: 'text-blue-400',
  invoice_paid: 'text-green-400',
  payment_received: 'text-green-400',
  event_registered: 'text-cyan-400',
  event_cancelled: 'text-orange-400',
  event_checkin: 'text-emerald-400',
  admin_impersonate: 'text-yellow-400',
  admin_change_approved: 'text-green-400',
  admin_change_rejected: 'text-red-400',
  sync_import: 'text-blue-400',
  sync_export: 'text-green-400',
  password_setup: 'text-violet-400',
  password_reset: 'text-violet-400',
};

const FILTER_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'login', label: 'Anmeldungen' },
  { value: 'login_failed', label: 'Fehlgeschlagen' },
  { value: 'invoice_sent', label: 'Rechnungen gesendet' },
  { value: 'invoice_paid', label: 'Rechnungen bezahlt' },
  { value: 'payment_received', label: 'Zahlungen' },
  { value: 'event_registered', label: 'Event-Anmeldungen' },
  { value: 'event_checkin', label: 'Check-ins' },
  { value: 'member_status_changed', label: 'Statusänderungen' },
  { value: 'admin_impersonate', label: 'Impersonierung' },
];

function formatDate(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();

  const time = d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Heute, ${time}`;
  if (isYesterday) return `Gestern, ${time}`;
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit' }) + `, ${time}`;
}

export default function AdminActivityLogPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const getToken = () => localStorage.getItem('shrt_token');

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 40 };
      if (filter) params.activity_type = filter;
      if (search) params.search = search;
      const res = await axios.get(`${API_URL}/api/activity-log`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params
      });
      setEntries(res.data.entries);
      setPages(res.data.pages);
      setTotal(res.data.total);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [page, filter, search]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Activity className="text-primary" size={28} />
            <div>
              <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight">
                Aktivitätslog
              </h1>
              <p className="text-xs text-muted-foreground">{total} Einträge</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Suche nach E-Mail, Name..."
                className="pl-9 rounded-none h-9"
                data-testid="activity-search"
              />
            </div>
            {search && (
              <Button type="button" variant="ghost" size="sm" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}>
                <X size={16} />
              </Button>
            )}
          </form>
          <div className="flex gap-2 flex-wrap">
            {FILTER_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => { setFilter(opt.value); setPage(1); }}
                className={`text-xs px-3 py-1.5 border transition-colors ${
                  filter === opt.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:border-primary/50 text-muted-foreground'
                }`}
                data-testid={`filter-${opt.value || 'all'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Log Entries */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Keine Aktivitäten gefunden
          </div>
        ) : (
          <div className="bg-card border border-border divide-y divide-border">
            {entries.map((entry, i) => {
              const Icon = TYPE_ICONS[entry.type] || Activity;
              const color = TYPE_COLORS[entry.type] || 'text-muted-foreground';
              return (
                <div key={i} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/20 transition-colors" data-testid={`log-entry-${i}`}>
                  <Icon size={18} className={`${color} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-medium truncate">
                        {entry.label}
                        {entry.target && <span className="text-muted-foreground font-normal"> — {entry.target}</span>}
                      </p>
                      <span className="text-[11px] text-muted-foreground whitespace-nowrap flex-shrink-0">{formatDate(entry.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">{entry.user_email}</span>
                      {entry.details && <span className="text-xs text-muted-foreground/60 truncate">· {entry.details}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="sm"
              className="rounded-none"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft size={16} />
            </Button>
            <span className="text-sm text-muted-foreground">
              Seite {page} von {pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="rounded-none"
              disabled={page >= pages}
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
